import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Divider,
  Avatar,
  IconButton,
  InputAdornment,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { AuthService, ProfileData } from '../services/AuthService';
import { ROLE_LABEL, normalizeRole } from '../utils/permissions';
import { getUserInfo, setUserInfo } from '../utils/auth';
import { withMediaToken } from '../utils/media';

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
  /** 이름 저장 후 부모(메뉴, 헤더 등)가 자체 state 를 동기화할 수 있도록 호출. */
  onUpdated?: (profile: ProfileData) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 프로필 조회 + 수정 모달.
 * - 역할·아이디(로그인 ID)는 read-only — 아이디는 관리자만 변경 가능.
 * - 이름·이메일은 편집 가능. 저장 시 백엔드가 JWT 를 재발급하고 AuthService 가 토큰/UserInfo 갱신.
 */
const ProfileDialog: React.FC<ProfileDialogProps> = ({ open, onClose, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailNotif, setEmailNotif] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);

  // 프로필 이미지 섹션 — 업로드/삭제. 백엔드가 ?v={fid} 캐시버스팅 URL 을 만들어
  // 보내므로 새 업로드 시 자동으로 캐시 무효화. localStorage 도 함께 갱신해
  // 헤더 아바타도 즉시 반영된다.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imgBusy, setImgBusy] = useState(false);
  const [imgError, setImgError] = useState<string | null>(null);

  // 비밀번호 변경 섹션 — 같은 다이얼로그 안에서 처리. 기존 비번이 일치할 때만
  // 새 비번으로 교체. JWT claim 변동 없으니 토큰 재발급은 불필요.
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSuccess, setPwdSuccess] = useState<string | null>(null);
  const [showPwd, setShowPwd] = useState(false);

  // 비밀번호 변경 3개 필드가 공유하는 표시/숨김 토글 어도먼트.
  const pwdToggleAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPwd ? '비밀번호 숨기기' : '비밀번호 표시'}
        onClick={() => setShowPwd((v) => !v)}
        onMouseDown={(e) => e.preventDefault()}
        edge="end"
        size="small"
      >
        {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  // dialog 닫힘 → 비번 입력 초기화 (다음 오픈 시 잔존 방지).
  useEffect(() => {
    if (open) return;
    setCurrentPwd('');
    setNewPwd('');
    setConfirmPwd('');
    setPwdError(null);
    setPwdSuccess(null);
  }, [open]);

  const newPwdValid = newPwd.length >= 6 && newPwd.length <= 128;
  const confirmMatches = confirmPwd.length > 0 && confirmPwd === newPwd;
  const canChangePwd =
    !pwdSubmitting &&
    currentPwd.length > 0 &&
    newPwdValid &&
    confirmMatches &&
    currentPwd !== newPwd;

  const handleChangePassword = async () => {
    if (!canChangePwd) return;
    setPwdSubmitting(true);
    setPwdError(null);
    setPwdSuccess(null);
    try {
      const resp = await AuthService.changePassword(currentPwd, newPwd);
      if (resp.success) {
        setPwdSuccess('비밀번호가 변경되었습니다.');
        setCurrentPwd('');
        setNewPwd('');
        setConfirmPwd('');
        setShowPwd(false);
      } else {
        setPwdError(resp.message || '비밀번호 변경 실패');
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message: unknown }).message)
          : '비밀번호 변경 실패';
      setPwdError(msg);
    } finally {
      setPwdSubmitting(false);
    }
  };

  // 모달 열릴 때마다 서버에서 최신 프로필 동기화. localStorage 만 믿지 않는다.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSuccess(null);
    AuthService.getProfile()
      .then((resp) => {
        if (cancelled) return;
        if (resp.success && resp.data) {
          setProfile(resp.data);
          setName(resp.data.name);
          setEmail(resp.data.email);
          setEmailNotif(resp.data.email_notifications_enabled);
        } else {
          setError(resp.message || '프로필을 불러올 수 없습니다.');
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '프로필 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const hasChanges =
    profile != null &&
    (trimmedName !== profile.name ||
      trimmedEmail !== profile.email ||
      emailNotif !== profile.email_notifications_enabled);
  const nameValid = trimmedName.length >= 2 && trimmedName.length <= 100;
  const emailValid = EMAIL_RE.test(trimmedEmail);
  const canSave = hasChanges && nameValid && emailValid && !saving;

  // 헤더 아바타 동기화 — UserInfo 의 profile_image_url 갱신. dialog 부모(메뉴)
  // 가 자체 state 를 강제 리렌더하도록 onUpdated 도 호출.
  const syncHeaderAvatar = (newUrl?: string) => {
    const cur = getUserInfo();
    if (cur) setUserInfo({ ...cur, profile_image_url: newUrl });
    if (profile) {
      const updated = { ...profile, profile_image_url: newUrl };
      setProfile(updated);
      onUpdated?.(updated);
    }
  };

  const handlePickFile = () => fileInputRef.current?.click();

  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // 같은 파일 재선택 가능하게 즉시 reset
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setImgError('이미지 파일만 업로드할 수 있습니다');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setImgError('파일 크기가 5MB를 초과합니다');
      return;
    }
    setImgError(null);
    setImgBusy(true);
    try {
      const resp = await AuthService.uploadProfileImage(f);
      if (resp.success) {
        syncHeaderAvatar(resp.profile_image_url);
      } else {
        setImgError(resp.message || '업로드 실패');
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : '업로드 실패';
      setImgError(msg);
    } finally {
      setImgBusy(false);
    }
  };

  const handleImageDelete = async () => {
    setImgError(null);
    setImgBusy(true);
    try {
      const resp = await AuthService.deleteProfileImage();
      if (resp.success) {
        syncHeaderAvatar(undefined);
      } else {
        setImgError(resp.message || '삭제 실패');
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : '삭제 실패';
      setImgError(msg);
    } finally {
      setImgBusy(false);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await AuthService.updateProfile(trimmedName, emailNotif, trimmedEmail);
      if (resp.success && resp.data) {
        setProfile(resp.data);
        setName(resp.data.name);
        setEmail(resp.data.email);
        setEmailNotif(resp.data.email_notifications_enabled);
        setSuccess('프로필이 저장되었습니다.');
        onUpdated?.(resp.data);
      } else {
        setError(resp.message || '저장 실패');
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : '저장 실패';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>내 프로필</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : profile ? (
          <Stack spacing={2.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={profile.profile_image_url ? withMediaToken(profile.profile_image_url) : undefined}
                sx={{ width: 64, height: 64, fontSize: '1.25rem', bgcolor: 'primary.main' }}
                imgProps={{ onError: (e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; } }}
              >
                {(profile.name || profile.email || '?').charAt(0)}
              </Avatar>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="이미지 업로드 (5MB 이내, image/*)">
                    <span>
                      <IconButton size="small" onClick={handlePickFile} disabled={imgBusy}>
                        <PhotoCameraIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="이미지 삭제">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleImageDelete}
                        disabled={imgBusy || !profile.profile_image_url}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  프로필 이미지
                </Typography>
              </Stack>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageSelected}
              />
            </Box>
            {imgError && <Alert severity="error">{imgError}</Alert>}
            <Box>
              <Typography variant="caption" color="text.secondary">
                권한
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  label={ROLE_LABEL[normalizeRole(profile.role)]}
                  color={profile.role === 'admin' ? 'error' : profile.role === 'editor' ? 'primary' : 'default'}
                  variant="outlined"
                />
              </Box>
            </Box>
            <Divider />
            <TextField
              label="아이디"
              value={profile.username}
              fullWidth
              disabled
              helperText="로그인 아이디는 관리자만 변경할 수 있습니다"
            />
            <TextField
              label="이름"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              error={trimmedName.length > 0 && !nameValid}
              helperText={
                trimmedName.length === 0
                  ? '이름을 입력하세요'
                  : !nameValid
                  ? '이름은 2~100자여야 합니다'
                  : ' '
              }
              autoFocus
            />
            <TextField
              label="이메일"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              error={trimmedEmail.length > 0 && !emailValid}
              helperText={
                trimmedEmail.length === 0
                  ? '이메일을 입력하세요'
                  : !emailValid
                  ? '올바른 이메일 형식이 아닙니다'
                  : ' '
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={emailNotif}
                  onChange={(e) => setEmailNotif(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">이메일 알림</Typography>
                  <Typography variant="caption" color="text.secondary">
                    @멘션되거나 담당자로 지정되면 메일로 알려드립니다 (끄면 인앱 알림만 수신)
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0, '& .MuiSwitch-root': { mt: 0.25, mr: 1 } }}
            />
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Divider sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                비밀번호 변경
              </Typography>
            </Divider>
            <TextField
              label="기존 비밀번호"
              type={showPwd ? 'text' : 'password'}
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              fullWidth
              autoComplete="current-password"
              InputProps={{ endAdornment: pwdToggleAdornment }}
            />
            <TextField
              label="새 비밀번호"
              type={showPwd ? 'text' : 'password'}
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              fullWidth
              autoComplete="new-password"
              error={newPwd.length > 0 && !newPwdValid}
              helperText={
                newPwd.length > 0 && !newPwdValid ? '6~128자' : ' '
              }
              InputProps={{ endAdornment: pwdToggleAdornment }}
            />
            <TextField
              label="새 비밀번호 확인"
              type={showPwd ? 'text' : 'password'}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              fullWidth
              autoComplete="new-password"
              error={confirmPwd.length > 0 && !confirmMatches}
              helperText={
                confirmPwd.length > 0 && !confirmMatches
                  ? '새 비밀번호와 일치하지 않습니다'
                  : ' '
              }
              InputProps={{ endAdornment: pwdToggleAdornment }}
            />
            <Button
              variant="outlined"
              onClick={handleChangePassword}
              disabled={!canChangePwd}
              sx={{ alignSelf: 'flex-end' }}
            >
              {pwdSubmitting ? '변경 중...' : '비밀번호 변경'}
            </Button>
            {pwdError && <Alert severity="error">{pwdError}</Alert>}
            {pwdSuccess && <Alert severity="success">{pwdSuccess}</Alert>}
          </Stack>
        ) : (
          <Alert severity="error">{error || '프로필을 불러올 수 없습니다.'}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          닫기
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {saving ? '저장 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileDialog;
